import { useEffect, useMemo, useState } from "react";
import DateTableBody from "./DataTableBody";
import DataTableHeader from "./DataTableHeader";
import EmptyState from "../pages/EmptyState";
import { fuzzySearchRows } from "../../../utils/search/fuzzySearch";
import StandardEmpty from "../empty/StandardEmpty";

function DataTable({
  title,
  description,
  headers,
  rows,
  rightElement,
  headerMiddleElement,
  filterElement,
  onFilterClick,
  getActionMenu,
  emptyStateTitle,
  emptyStateText,
  preserveStructureOnEmpty = false,
  searchQuery = "",
  searchHeaders = [],
  rowDefaults = {},
  currentPage: controlledCurrentPage,
  totalPages: controlledTotalPages,
  onPageChange,
  copy,
}) {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const rowsPerPage = 10; // Adjust this to show more or fewer rows
  const isPaginationControlled =
    Number.isInteger(controlledCurrentPage) &&
    Number.isInteger(controlledTotalPages) &&
    typeof onPageChange === "function";
  const currentPage = isPaginationControlled
    ? controlledCurrentPage
    : internalCurrentPage;
  const searchableRows = useMemo(
    () => fuzzySearchRows(rows, searchQuery, searchHeaders),
    [rows, searchQuery, searchHeaders],
  );
  const sortedRows = useMemo(() => {
    if (!sortConfig) return searchableRows;

    const { key, direction } = sortConfig;
    const sortKey = `${key}_value`;

    const getSortValue = (row) => {
      const value = row[sortKey] ?? row[key];

      if (typeof value === "number") return value;
      if (value instanceof Date) return value.getTime();
      if (!value) return "";

      const stringValue = String(value);
      const numericValue = Number(stringValue.replace(/[^0-9.-]+/g, ""));
      if (stringValue.match(/[$,]/) && !Number.isNaN(numericValue)) {
        return numericValue;
      }

      const dateValue = Date.parse(stringValue);
      if (!Number.isNaN(dateValue)) return dateValue;

      return stringValue.toLowerCase();
    };

    return [...searchableRows].sort((firstRow, secondRow) => {
      const firstValue = getSortValue(firstRow);
      const secondValue = getSortValue(secondRow);

      if (firstValue < secondValue) return direction === "asc" ? -1 : 1;
      if (firstValue > secondValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [searchableRows, sortConfig]);

  const handleSort = (header) => {
    if (!header.sortable) return;

    if (isPaginationControlled) {
      onPageChange(1);
    } else {
      setInternalCurrentPage(1);
    }
    setSortConfig((currentSort) => ({
      key: header.key,
      direction:
        currentSort?.key === header.key && currentSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  useEffect(() => {
    if (!isPaginationControlled) {
      setInternalCurrentPage(1);
    }
  }, [isPaginationControlled, rows, searchQuery]);

  // Logic to calculate pagination
  const totalPages = isPaginationControlled
    ? controlledTotalPages
    : Math.ceil(sortedRows.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = isPaginationControlled
    ? sortedRows
    : sortedRows.slice(indexOfFirstRow, indexOfLastRow);
  const handlePageChange = isPaginationControlled
    ? onPageChange
    : (page) => setInternalCurrentPage(page);
  return (
    <div className="standard-data-table mobile-reset-standard-table-container">
      <DataTableHeader
        title={title}
        description={description}
        rightElement={rightElement}
        headerMiddleElement={headerMiddleElement}
      />
      {searchableRows.length > 0 || preserveStructureOnEmpty ? (
        <DateTableBody
          headers={headers}
          rows={currentRows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          sortConfig={sortConfig}
          onSort={handleSort}
          filterElement={filterElement}
          onFilterClick={onFilterClick}
          getActionMenu={getActionMenu}
          rowDefaults={rowDefaults}
          emptyStateText={emptyStateText}
          copy={copy}
        />
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center", // Horizontal center
            alignItems: "center", // Vertical center
            width: "100%",
            padding: "40px 20px",
          }}
        >
          <StandardEmpty msg={emptyStateText} />
        </div>
      )}
    </div>
  );
}

export default DataTable;
